
import React, { useState } from 'react';
import { User, Role, Theme } from '../types';

interface SidebarProps {
  activeView: string;
  setView: (view: any) => void;
  user: User;
  reviewCount: number;
  theme: Theme;
  setTheme: (theme: Theme) => void;
  onLogout: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ activeView, setView, user, reviewCount, theme, setTheme, onLogout }) => {
  const [logoError, setLogoError] = useState(false);
  
  const navItems = [
    { id: 'dashboard', label: 'Current Events', icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" /></svg>
    )},
    { id: 'discovery', label: 'Find Events', icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
    )},
    { id: 'review', label: 'Review Events', icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" /></svg>
    ), badge: reviewCount },
  ];

  if (user.role === Role.ADMIN) {
    navItems.push({ id: 'admin', label: 'Core Configuration', icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
    )});
  }

  return (
    <div className="w-72 flex flex-col bg-charcoal border-r border-white/5 z-20 transition-all duration-400">
      <div className="p-8 pb-8">
        {!logoError ? (
          <img 
            src="https://files.catbox.moe/w8074t.png" 
            alt="EventPulse" 
            className="h-10 w-auto object-contain" 
            onError={() => setLogoError(true)}
            referrerPolicy="no-referrer"
          />
        ) : (
          <div className="flex items-center gap-3.5">
            <div className="w-9 h-9 bg-accent rounded-lg flex items-center justify-center text-charcoal shadow-[0_0_15px_rgba(139,62,255,0.2)]">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
            </div>
            <div className="flex flex-col justify-center">
               <div className="text-lg font-black italic tracking-tighter uppercase text-white leading-none">EventPulse</div>
            </div>
          </div>
        )}
      </div>

      <nav className="flex-1 px-5 space-y-1.5">
        <div className="px-4 mb-5 text-[10px] font-bold text-slate-600 uppercase tracking-[0.3em]">Management</div>
        {navItems.map(item => (
          <button
            key={item.id}
            onClick={() => setView(item.id)}
            className={`w-full flex items-center justify-between px-4 py-4 rounded-xl transition-all duration-300 group relative ${
              activeView === item.id 
                ? 'bg-[#6a26d9] text-white shadow-lg' 
                : 'text-slate-500 hover:bg-white/[0.03] hover:text-slate-200'
            }`}
          >
            <div className="flex items-center gap-4">
              <span className={`transition-all duration-300 ${activeView === item.id ? 'text-accent scale-110' : 'text-slate-600 group-hover:text-slate-400'}`}>
                {item.icon}
              </span>
              <span className={`text-[11px] font-bold uppercase tracking-widest transition-all ${activeView === item.id ? 'translate-x-1' : ''}`}>{item.label}</span>
            </div>
            {item.badge ? (
              <span className="bg-accent text-black text-[9px] font-black px-1.5 py-0.5 rounded shadow-sm">
                {item.badge}
              </span>
            ) : null}
            {activeView === item.id && (
              <div className="absolute left-0 inset-y-4 w-1 bg-accent rounded-r-full shadow-[0_0_12px_rgba(139,62,255,0.4)]"></div>
            )}
          </button>
        ))}
      </nav>

      <div className="px-8 py-8 border-t border-white/5 space-y-5 bg-black/10">
        <button 
          onClick={onLogout}
          className="w-full flex items-center gap-4 px-3 py-2 text-slate-500 hover:text-accent transition-all duration-300 group"
        >
          <svg className="w-4 h-4 transition-transform group-hover:rotate-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
          <span className="text-[10px] font-bold uppercase tracking-widest">End Session</span>
        </button>

        <div className="flex items-center justify-between p-1.5 rounded-xl bg-charcoal border border-white/5 shadow-inner">
           <button 
             onClick={() => setTheme('light')}
             className={`flex-1 flex justify-center py-2.5 rounded-lg transition-all duration-300 ${theme === 'light' ? 'bg-white text-charcoal shadow-md scale-[1.05]' : 'text-slate-600 hover:text-white'}`}
             title="Daylight Mode"
           >
             <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 3v1m0 16v1m9-9h-1M4 9h-1m15.364-6.364l-.707.707M6.343 17.657l-.707.707m12.728 0l-.707-.707M6.343 6.343l-.707-.707M12 8a4 4 0 100 8 4 4 0 000-8z" /></svg>
           </button>
           <button 
             onClick={() => setTheme('dark')}
             className={`flex-1 flex justify-center py-2.5 rounded-lg transition-all duration-300 ${theme === 'dark' ? 'bg-accent text-black shadow-md shadow-accent/20 scale-[1.05]' : 'text-slate-600 hover:text-white'}`}
             title="Dark Protocol"
           >
             <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" /></svg>
           </button>
        </div>
      </div>

      <div className="p-8 border-t border-white/5 bg-black/40">
        <div className="flex items-center gap-4">
          <div className="relative">
            <img src={user.avatar} className="w-11 h-11 rounded-xl border border-white/10 grayscale hover:grayscale-0 transition-all cursor-pointer shadow-lg" alt={user.name} />
            <div className="absolute -bottom-1 -right-1 w-3.5 h-3.5 bg-accent border-2 border-charcoal rounded-full"></div>
          </div>
          <div className="flex flex-col overflow-hidden">
            <span className="text-xs font-bold truncate text-white uppercase tracking-widest">{user.name}</span>
            <span className="text-[9px] font-bold text-slate-500 tracking-[0.2em] uppercase mt-0.5">{user.role}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
